import Vue from "vue";
import { mapState } from "vuex";
import NotificationService from "../../../../ceres/services/NotificationService";
import TranslationService from "../../../../ceres/services/TranslationService";
import AddressSelect from "./AddressSelect";

export default Vue.component("shipping-address-select", {
	components: {
		AddressSelect
	},

	template: `
        <address-select
            ref:shipping-address-select
            template="#vue-address-select"
            @address-changed="addressChanged"
            address-type="2"
            :optional-address-fields="optionalAddressFields"
            :required-address-fields="requiredAddressFields"
            :default-salutation="defaultSalutation"
            :padding-classes="paddingClasses"
            :padding-inline-styles="paddingInlineStyles"
            :is-invalid-shipping-country="isInvalidShippingCountry"
            data-testing="delivery-address-select"
			:email="email"
			:show-pre-button="showPreButton"
			:fav-countries="favCountries"
			:alternate-translations="alternateTranslations"
			:fields-by-country="fieldsByCountry">
			<template #title>
				<slot name="title"></slot>
			</template>
        </address-select>
    `,

	props: {
		optionalAddressFields: {
			type: Object,
			default: () => {
				return {};
			}
		},
		requiredAddressFields: {
			type: Object,
			default: () => {
				return {};
			}
		},
		defaultSalutation: {
			type: String,
			default: App.config.addresses.defaultSalutation
		},
		hasAnyPostOfficePreset: {
			type: Boolean,
			default: false
		},
		hasAnyParcelBoxPreset: {
			type: Boolean,
			default: false
		},
		paddingClasses: {
			type: String,
			default: null
		},
		paddingInlineStyles: {
			type: String,
			default: null
		},
		email: String,
		showPreButton: {
			type: Boolean,
			default: false
		},
		favCountries: {
			type: Array,
			default: () => []
		},
		alternateTranslations: {
			type: Object,
			default: () => {
				return {};
			}
		},
		fieldsByCountry: {
			type: Object,
			default: () => {
				return {};
			}
		}
	},

	data() {
		return {
			isInvalidShippingCountry: false
		};
	},
	computed: {
		filteredShippingCountries() {
			return App.Eros.Config.general.visibleShippingCountries.length
				? this.shippingCountryList.filter(shippingCountry => {
						return App.Eros.Config.general.visibleShippingCountries.includes(shippingCountry.isoCode2);
				  })
				: this.shippingCountryList;
		},

		...mapState({
			shippingCountryList: state => state.localization.shippingCountries,
			billingAddress: state => state.address.billingAddress,
			billingAddressId: state => state.address.billingAddressId,
			deliveryAddress: state => state.address.deliveryAddress,
			deliveryAddressId: state => state.address.deliveryAddressId
		})
	},

	created() {
		this.$store.commit("setDeliveryAddressValidator", this.validate);
	},

	mounted() {
		if (App.templateType === "my-account") {
			if (this.hasAnyParcelBoxPreset) {
				this.$store.commit("setParcelBoxAvailability", true);
			}
			if (this.hasAnyPostOfficePreset) {
				this.$store.commit("setPostOfficeAvailability", true);
			}
		}

		this.checkDeliveryAddressError();
	},

	methods: {
		/**
		 * Update the delivery address
		 * @param selectedAddress
		 */
		addressChanged(selectedAddress) {
			this.$store.dispatch("selectAddress", { selectedAddress, addressType: "2" }).then(
				response => {
					document.dispatchEvent(
						new CustomEvent("afterDeliveryAddressChanged", { detail: this.deliveryAddressId })
					);
					this.checkDeliveryAddressError();
				},
				error => {}
			);
		},

		validate() {
			const selectedBillingAddress = this.$store.state.address.billingAddress;
			const selectedDeliveryAddress = this.$store.state.address.deliveryAddress;
			const activeShippingCountries = this.$store.state.localization.shippingCountries;

			const countryId =
				Number(selectedDeliveryAddress.id) === -99
					? selectedBillingAddress.countryId
					: selectedDeliveryAddress.countryId;
			const isAllowedForShipping = !!activeShippingCountries.find(country => country.id === countryId);

			this.$store.commit("setDeliveryAddressShowError", !isAllowedForShipping);

			if (!isAllowedForShipping) {
				NotificationService.error(
					TranslationService.translate("Ceres::Template.checkoutInvalidShippingCountry")
				);
			}
		},

		checkDeliveryAddressError() {
			if (this.billingAddress !== null) {
				const countryId = Number(this.deliveryAddress.id) === -99 ? this.billingAddress?.countryId : this.deliveryAddress?.countryId;

				if (countryId) {
					const validShippingCountry = this.filteredShippingCountries.find((country) => country.id === countryId);

					this.isInvalidShippingCountry = !validShippingCountry;
				}
			}
		}
	},

	watch: {
		billingAddress() {
			// if a delivery address exists do not take into account the billingAddress
			if (Number(this.deliveryAddressId) === -99) {
				this.checkDeliveryAddressError();
			}
		},

		deliveryAddress() {
			this.checkDeliveryAddressError();
		}
	}
});
